/** Демо-бэкенд в браузере: поддельный сервер, шаблоны в localStorage, имитация отправки. */
import { uid } from '../../src/shared/factory.js';
import { hasErrors, validateDraft } from '../../src/shared/validate.js';
import type { Draft } from '../../src/shared/types.js';
import { ApiError, type GuildData, type Template, type TemplateFolder } from './api.js';

const GID = '1180000000000000001';
const id = (n: number) => `11800000000000${String(n).padStart(5, '0')}`;

const guild: GuildData = {
  id: GID,
  name: 'Демо сервер',
  iconUrl: null,
  canManage: true,
  canManageRoles: true,
  userTopRole: 50,
  botTopRole: 40,
  bot: { id: id(900), username: 'Embed Studio', avatarUrl: 'https://cdn.discordapp.com/embed/avatars/1.png' },
  me: { id: id(901), username: 'Вы', avatarUrl: 'https://cdn.discordapp.com/embed/avatars/2.png' },
  channels: [
    { id: id(1), name: 'Информация', type: 4, parentId: null, position: 0, canView: true, userCanSend: true, botCanSend: true, botCanWebhook: true, tags: [] },
    { id: id(2), name: 'правила', type: 0, parentId: id(1), position: 1, canView: true, userCanSend: true, botCanSend: true, botCanWebhook: true, tags: [] },
    { id: id(3), name: 'новости', type: 5, parentId: id(1), position: 2, canView: true, userCanSend: true, botCanSend: true, botCanWebhook: true, tags: [] },
    { id: id(4), name: 'Общение', type: 4, parentId: null, position: 3, canView: true, userCanSend: true, botCanSend: true, botCanWebhook: true, tags: [] },
    { id: id(5), name: 'общий', type: 0, parentId: id(4), position: 4, canView: true, userCanSend: true, botCanSend: true, botCanWebhook: true, tags: [] },
    {
      id: id(6),
      name: 'идеи',
      type: 15,
      parentId: id(4),
      position: 5,
      canView: true,
      userCanSend: true,
      botCanSend: true,
      botCanWebhook: true,
      tags: [
        { id: id(60), name: 'Предложение', emoji: '💡' },
        { id: id(61), name: 'Баг', emoji: '🐞' },
      ],
    },
    { id: id(7), name: 'Голосовой', type: 2, parentId: id(4), position: 6, canView: true, userCanSend: true, botCanSend: true, botCanWebhook: true, tags: [] },
  ],
  threads: [{ id: id(8), name: 'Обсуждение обновления', parentId: id(5), type: 11 }],
  roles: [
    { id: id(20), name: 'Администратор', color: 0xed4245, position: 50, managed: false, everyone: false },
    { id: id(21), name: 'Embed Studio', color: 0x7c5cff, position: 40, managed: true, everyone: false },
    { id: id(22), name: 'Модератор', color: 0x3ba55d, position: 30, managed: false, everyone: false },
    { id: id(23), name: 'Участник', color: 0x5865f2, position: 10, managed: false, everyone: false },
    { id: id(24), name: 'Новости', color: 0xf0b232, position: 5, managed: false, everyone: false },
    { id: GID, name: '@everyone', color: 0, position: 0, managed: false, everyone: true },
  ],
  emojis: [],
  settings: { allowedRoles: [id(22)], logChannelId: null, folderAdminRoles: [] },
  templates: [],
  folders: [],
  canManageFolders: true,
};

const KEY = 'es:demo:templates';

function loadTemplates(): Template[] {
  try {
    return JSON.parse(localStorage.getItem(KEY) ?? '[]');
  } catch {
    return [];
  }
}
function saveTemplates(list: Template[]) {
  try {
    localStorage.setItem(KEY, JSON.stringify(list));
  } catch {
    /* приватный режим */
  }
}

let templates = loadTemplates();

const FKEY = 'es:demo:folders';
function loadFolders(): TemplateFolder[] {
  try {
    return JSON.parse(localStorage.getItem(FKEY) ?? '[]');
  } catch {
    return [];
  }
}
function saveFolders() {
  try {
    localStorage.setItem(FKEY, JSON.stringify(folders));
  } catch {
    /* приватный режим */
  }
}
let folders = loadFolders();
const meta = (t: Template) => {
  const { data: _d, ...m } = t;
  return { ...m, folderId: m.folderId ?? null };
};
const delay = (ms: number) => new Promise((r) => setTimeout(r, ms));

export async function mockTransport(method: string, path: string, body?: any): Promise<any> {
  await delay(120);
  const p = path.split('?')[0];
  if (p === '/api/me') {
    return { user: { id: guild.me.id, username: 'demo', globalName: 'Демо пользователь', avatarUrl: guild.me.avatarUrl }, oauth: false, inviteUrl: null, ready: true };
  }
  if (p === '/api/logout') return { ok: true };
  if (p === '/api/guilds') return [{ id: GID, name: guild.name, iconUrl: null, canManage: true }];
  if (p === `/api/guilds/${GID}`) return { ...guild, templates: templates.map(meta), folders };
  if (p === `/api/guilds/${GID}/settings` && method === 'PUT') {
    guild.settings = { allowedRoles: body.allowedRoles, logChannelId: body.logChannelId, folderAdminRoles: body.folderAdminRoles ?? [] };
    return guild.settings;
  }
  if (p === `/api/guilds/${GID}/folders`) {
    if (method === 'GET') return folders;
    const name = String(body.name).trim();
    if (folders.some((f) => f.name.toLowerCase() === name.toLowerCase())) throw new ApiError(409, `Папка «${name}» уже существует`);
    const now = Date.now();
    const f: TemplateFolder = { id: uid(10), name, emoji: body.emoji || null, viewRoles: body.viewRoles ?? null, editRoles: body.editRoles ?? null, sort: folders.length + 1, createdBy: guild.me.id, createdAt: now, updatedAt: now, canView: true, canEdit: true };
    folders.push(f);
    saveFolders();
    return f;
  }
  const fm = new RegExp(`^/api/guilds/${GID}/folders/(\\w+)$`).exec(p);
  if (fm) {
    const f = folders.find((x) => x.id === fm[1]);
    if (!f) throw new ApiError(404, 'Папка не найдена');
    if (method === 'DELETE') {
      let moved = 0;
      for (const t of templates) if (t.folderId === f.id) (t.folderId = null), moved++;
      folders = folders.filter((x) => x !== f);
      saveFolders();
      saveTemplates(templates);
      return { ok: true, moved };
    }
    if (body.name !== undefined) {
      if (folders.some((x) => x !== f && x.name.toLowerCase() === String(body.name).toLowerCase())) throw new ApiError(409, 'Такое название уже занято');
      f.name = String(body.name).trim();
    }
    if (body.emoji !== undefined) f.emoji = body.emoji || null;
    if (body.viewRoles !== undefined) f.viewRoles = body.viewRoles;
    if (body.editRoles !== undefined) f.editRoles = body.editRoles;
    f.updatedAt = Date.now();
    saveFolders();
    return f;
  }
  if (p === `/api/guilds/${GID}/templates`) {
    if (method === 'GET') return templates.map(meta);
    const name = String(body.name).trim();
    if (templates.some((t) => t.name.toLowerCase() === name.toLowerCase())) throw new ApiError(409, `Шаблон «${name}» уже существует`);
    const now = Date.now();
    const t: Template = { id: uid(10), name, data: body.data as Draft, createdAt: now, updatedAt: now, createdBy: guild.me.id, updatedBy: guild.me.id, mode: (body.data as Draft).mode, folderId: body.folderId ?? null };
    templates.unshift(t);
    saveTemplates(templates);
    return t;
  }
  const tm = new RegExp(`^/api/guilds/${GID}/templates/(\\w+)$`).exec(p);
  if (tm) {
    const t = templates.find((x) => x.id === tm[1]);
    if (!t) throw new ApiError(404, 'Шаблон не найден');
    if (method === 'GET') return { ...t, folderId: t.folderId ?? null, canEdit: true };
    if (method === 'DELETE') {
      templates = templates.filter((x) => x.id !== t.id);
      saveTemplates(templates);
      return { ok: true };
    }
    if (body.name) {
      if (templates.some((x) => x.id !== t.id && x.name.toLowerCase() === body.name.toLowerCase())) throw new ApiError(409, 'Такое название уже занято');
      t.name = body.name;
    }
    if (body.data) {
      t.data = body.data;
      t.mode = body.data.mode;
    }
    if (body.folderId !== undefined) t.folderId = body.folderId;
    t.updatedAt = Date.now();
    templates.sort((a, b) => b.updatedAt - a.updatedAt);
    saveTemplates(templates);
    return t;
  }
  if (p === `/api/guilds/${GID}/send`) {
    await delay(500);
    const issues = validateDraft(body.draft, { edit: body.edit ? { wasV2: body.edit.wasV2 } : undefined, target: body.edit?.via ?? body.target?.via });
    if (hasErrors(issues)) throw new ApiError(400, 'Сообщение не прошло проверку', issues);
    const channelId = body.edit?.channelId ?? body.target?.threadId ?? body.target?.channelId;
    const messageId = body.edit?.messageId ?? `13${Date.now()}`.slice(0, 19);
    return {
      messageId,
      channelId,
      url: `https://discord.com/channels/${GID}/${channelId}/${messageId}`,
      editTarget: body.edit ?? { channelId, messageId, via: body.target.via, wasV2: body.draft.mode === 'v2' },
    };
  }
  if (p === `/api/guilds/${GID}/load-message`) throw new ApiError(400, 'В демо-режиме загрузка сообщений по ссылке недоступна - используйте импорт JSON');
  if (p.startsWith(`/api/guilds/${GID}/drafts/`)) throw new ApiError(404, 'Черновик не найден');
  throw new ApiError(404, 'Не найдено');
}
